import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  CheckSquare,
  PlusCircle,
  List,
  ClipboardCheck,
  BarChart2,
  Settings,
  Eye,
} from 'lucide-react';
import { useAuth, RoleType } from '../context/AuthContext';

interface SidebarProps {
  open: boolean;
  onClose?: () => void;
}

const roleDisplayNames: Record<RoleType, string> = {
  admin: 'Administrator',
  creator: 'Feedback Creator',
  respondent: 'Feedback Taker',
};

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const { isAdmin, effectiveRole, viewAsRole, setViewAsRole } = useAuth();

  const navItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: LayoutDashboard,
      roles: ['creator', 'admin'],
    },
    {
      name: 'My Feedback',
      href: '/feedback/assigned',
      icon: CheckSquare,
      roles: ['respondent', 'creator', 'admin'],
    },
    {
      name: 'Create Feedback',
      href: '/feedback/create',
      icon: PlusCircle,
      roles: ['creator', 'admin'],
    },
    {
      name: 'Feedback List',
      href: '/feedback/list',
      icon: List,
      roles: ['creator', 'admin'],
    },
    {
      name: 'Instances',
      href: '/instances',
      icon: ClipboardCheck,
      roles: ['creator', 'admin'],
    },
    {
      name: 'Results',
      href: '/results',
      icon: BarChart2,
      roles: ['creator', 'admin'],
    },
    {
      name: 'Administration',
      href: '/administration',
      icon: Settings,
      roles: ['admin'],
    },
  ];

  const filteredNavItems = navItems.filter(
    (item) => effectiveRole && item.roles.includes(effectiveRole)
  );

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-gray-900 dark:bg-gray-950
          transform transition-transform duration-300 ease-in-out
          lg:transform-none
          ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${!open && 'lg:w-0 lg:overflow-hidden'}
        `}
      >
        <div className="h-full flex flex-col">
          <div className="px-4 py-6 border-b border-gray-800 text-center">
            <h1 className="text-2xl font-bold text-white">Feedback</h1>
          </div>

          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {filteredNavItems.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => {
                  if (window.innerWidth < 1024) {
                    onClose?.();
                  }
                }}
                className={({ isActive }) =>
                  `flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`
                }
              >
                <item.icon className="mr-3 h-5 w-5" />
                <span className="whitespace-nowrap">{item.name}</span>
              </NavLink>
            ))}
          </nav>

          {isAdmin && (
            <div className="px-4 py-4 border-t border-gray-800">
              <div className="flex items-center mb-2 text-xs font-medium text-gray-400 uppercase tracking-wider">
                <Eye className="mr-2 h-4 w-4" />
                <span>View As</span>
              </div>
              <select
                value={viewAsRole || ''}
                onChange={(e) => setViewAsRole(e.target.value as RoleType || null)}
                className="w-full px-3 py-2 text-sm bg-gray-800 text-gray-200 rounded-md border border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Default (Admin)</option>
                <option value="creator">Feedback Creator</option>
                <option value="respondent">Feedback Taker</option>
              </select>
              {viewAsRole && (
                <p className="mt-2 text-xs text-amber-400">
                  Viewing as: {roleDisplayNames[viewAsRole]}
                </p>
              )}
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;